import { ChevronDown } from 'lucide-react';
import React from 'react';
import { Badge } from '@components/ui/badge';
import { Checkbox } from '@components/ui/checkbox';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger
} from '@components/ui/collapsible';
import { I18n } from '../../../core/i18n';
import { injectTSDI } from '../../../core/tsdi';
import { ReactComponent as Step } from '../../../../assets/svg/icons/steps.svg';
import { ReactComponent as Studio } from '../../../../assets/svg/icons/target.svg';
import { ReactComponent as Workout } from '../../../../assets/svg/icons/workout.svg';
import { FitnessActivityDetails } from './fitness-activity-details';
import { GpxAnalysisChip } from '../../../components/gpx-analysis/GpxAnalysisChip';
import { ActivityDto } from '../../../core/api/FitnessActivityClient';
import { formatDateTimeLocal } from './utils';

interface FitnessActivityProps {
    fitnessActivity: ActivityDto;
    onChange?: () => void;
    selected?: boolean;
    onSelectionChange?: (activityId: number, selected: boolean) => void;
}

function getIconType(activityType: ActivityDto['type']) {
    switch (activityType) {
        case 'DAILY_STEPS':
            return { icon: Step, viewBox: '0 0 13 22' };
        case 'FACILITY_VISIT':
            return { icon: Studio, viewBox: '0 0 22 22' };
        default:
            return { icon: Workout, viewBox: '0 0 20 20' };
    }
}

export const FitnessActivity = ({
    fitnessActivity,
    onChange,
    selected = false,
    onSelectionChange
}: FitnessActivityProps) => {
    const { currentLocale } = injectTSDI(I18n);
    const { icon: Icon, viewBox } = getIconType(fitnessActivity.type);
    const [expanded, setExpanded] = React.useState<boolean>(false);
    return (
        <Collapsible asChild open={expanded} onOpenChange={setExpanded}>
            <li>
                <div className="flex items-center gap-3 pl-4">
                    {onSelectionChange && (
                        <Checkbox
                            checked={selected}
                            disabled={fitnessActivity.rejected}
                            aria-label={
                                fitnessActivity.rejected
                                    ? `Activity ${fitnessActivity.id} is already rejected`
                                    : `Select activity ${fitnessActivity.id} for rejection`
                            }
                            onCheckedChange={(checked) =>
                                onSelectionChange(
                                    fitnessActivity.id,
                                    checked === true
                                )
                            }
                        />
                    )}
                    <CollapsibleTrigger className="flex min-w-0 flex-1 items-center gap-4 py-4 pr-4 pl-2 text-left text-sm hover:bg-accent/50 [&[data-state=open]>svg]:rotate-180">
                        <span className="w-52 shrink-0 whitespace-nowrap tabular-nums">
                            {formatDateTimeLocal(
                                fitnessActivity.timestamp,
                                currentLocale
                            )}
                        </span>
                        <span className="flex min-w-0 flex-1 items-center gap-3 text-muted-foreground">
                            <Icon
                                viewBox={viewBox}
                                className="size-5 shrink-0 fill-current"
                            />
                            <span className="truncate">
                                {fitnessActivity.type}
                                {fitnessActivity.workoutCategory
                                    ? ` · ${fitnessActivity.workoutCategory}`
                                    : ''}
                                {fitnessActivity.workoutTypeV2
                                    ? ` · ${fitnessActivity.workoutTypeV2}`
                                    : ''}
                            </span>
                        </span>
                        <span className="flex shrink-0 items-center justify-end gap-2">
                            <GpxAnalysisChip
                                action={fitnessActivity.gpxAnalysisAction}
                            />
                            {fitnessActivity.rejected ? (
                                <Badge variant="destructive">rejected</Badge>
                            ) : (
                                <Badge variant="success">approved</Badge>
                            )}
                        </span>
                        <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform" />
                    </CollapsibleTrigger>
                </div>
                <CollapsibleContent className="border-t bg-muted/20 px-4 pt-4 pb-4">
                    {fitnessActivity && fitnessActivity.id && expanded && (
                        <FitnessActivityDetails
                            fitnessActivityId={fitnessActivity.id}
                            onChange={onChange}
                        />
                    )}
                </CollapsibleContent>
            </li>
        </Collapsible>
    );
};
